import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Product } from '../../../../../../../../public-session/src/graphql';
import { BehaviorSubject, Subject } from 'rxjs';
import { first, map, takeUntil, tap } from 'rxjs/operators';
import { ZalamoDialogService } from '../../../../zalamo-dialog/zalamo-dialog.service';
import { NewProjectNameComponent } from '../new-project-name/new-project-name.component';
import { select, Store } from '@ngrx/store';
import {
  addProductToBasketAction,
  changeProductInBasketAction,
} from '../../../shopping-cart-back/actions/actions';
import { normalizeProductConfig } from '../product-configuration/helpers';
import { MissingPhotosListComponent } from '../missing-photos-list/missing-photos-list.component';
import {
  CustomProductAttributeConfig,
  Product as TypedProduct,
} from 'projects/zalamo-library/src/lib/ui/products/types';
import {
  selectConfigSell,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectProductsInBasket,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/selector';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { FullscreenService } from 'ZalamoLibrary/src/lib/ui/fullscreen/fullscreen.service';
import { TooltipRef } from 'ZalamoLibrary/src/lib/ui/zalamo-tooltip/tooltip-ref';
import { ZalamoTooltipService } from 'ZalamoLibrary/src/lib/ui/zalamo-tooltip/zalamo-tooltip.service';
import { ZalamoObject } from 'ZalamoLibrary/src/lib/zalamo/pipes/names/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { AutoAddFileToBasketTogetherWithProductService } from 'ZalamoLibrary/src/lib/ui/products/products-front/components/product-card/auto-add-file-to-basket-together-with-product.service';
import { ListOfPhotosToChooseFromInProductService } from 'ZalamoLibrary/src/lib/ui/products/products-front/components/product-card/list-of-photos-to-choose-from-in-product.service';
import cuid from 'cuid';
import { translationProvider } from '@ui/nls/translations/translate.directive';

type ProductDefinition = Omit<Product, 'organizationId' | 'sortOrder'> &
  Pick<TypedProduct, 'config' | 'configurationImages' | 'composedImages'>;

@Component({
    selector: 'lib-product-card',
    templateUrl: './product-card.component.html',
    styleUrls: ['./product-card.component.scss'],
    providers: [translationProvider('productFront')],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductCardComponent extends AliveState implements OnInit {
  @ViewChild('saveButton', { static: false })
  saveButton: ElementRef<HTMLElement>;
  @ViewChild('photoSelectButton', { static: false })
  photoSelectButton: ElementRef<HTMLElement>;
  @Input() product: ProductDefinition;
  @Input() @Output() fixingAttributes = false;
  @Input() selectedProductId = '';
  @Input() nameClientProduct = '';
  @Input() swap = false;
  @Input() photoIdList: Array<{ photoId: string }>;
  @Input() @Required context: ZalamoObject;
  @Input() @Required contextId: string;
  @Input() configuration = new BehaviorSubject<{
    [x: string]: string | boolean;
  }>({});
  @Input() preConfiguration = new BehaviorSubject({});
  @Output() hideDialog = new EventEmitter();
  currentConfig = new BehaviorSubject<ProductDefinition>(null);
  @ViewChild('dump') dump: TemplateRef<any>;
  preConfig = new BehaviorSubject(null);
  invalidFields$ = new BehaviorSubject([]);
  @Input() quantity = 1;
  configSell$ = this.store.pipe(select(selectConfigSell));
  photosList$ = this.store.pipe(select(selectPhotosList));
  photoGroups$ = this.store.pipe(select(selectPhotoGroups));
  filesInBasket$ = this.store.pipe(select(selectFilesInBasket));
  packagesInBasket$ = this.store.pipe(select(selectPackagesInBasket));
  productsInBasket$ = this.store.pipe(select(selectProductsInBasket));
  filtered = new BehaviorSubject<
    Array<CustomProductAttributeConfig & { fixing: boolean }>
  >([]);
  images = {
    imagesRequired: false,
    requiredImages: { max: 0, min: 0 },
  };
  openPhotosList$ = new Subject<boolean>();
  isPhonePortrait$ = this.screenObserver
    .observe(Breakpoints.Handset)
    .pipe(map(({ matches }) => matches));
  @ViewChild('message')
  message;
  @ViewChild('messageAboutAddedToBasket')
  messageAboutAddedToBasket;
  tooltipRef: TooltipRef<any> = null;

  constructor(
    private screenObserver: BreakpointObserver,
    private dialogService: ZalamoDialogService,
    private store: Store,
    private fullscreenService: FullscreenService,
    private tooltipService: ZalamoTooltipService,
    private autoAddFileToBasketTogetherWithProductService: AutoAddFileToBasketTogetherWithProductService,
    private listOfPhotosToChooseFromInProductService: ListOfPhotosToChooseFromInProductService
  ) {
    super();
  }

  ngOnInit() {
    if (this.product.imagesRequired) {
      this.images.imagesRequired = this.product.imagesRequired;
      this.images.requiredImages = this.product.requiredImages;
    }
    this.subscribeWhileAlive(
      this.filtered.pipe(
        tap((filtered) => {
          if (
            !filtered
              .filter(({ type }) => type !== 'boolean')
              .find(({ fixing }) => !fixing)
          ) {
            if (this.tooltipRef) {
              this.tooltipRef.close();
              this.tooltipRef = null;
            }
          }
        })
      ),
      this.configuration.pipe(
        tap((x) => {
          const configuration = Object.entries(x);
          const config = this.product.config
            .filter(({ type }) => ['list', 'size', 'decoration'].includes(type))
            .reverse()
            .find(({ customId, options }) =>
              options.find(
                ({ configId, hasRequiredImagesModifier }) =>
                  configId ===
                  configuration.find(
                    (value) =>
                      value[0] === customId && !!hasRequiredImagesModifier
                  )?.[1]
              )
            );
          const option = config?.options.find(
            ({ configId }) =>
              configId ===
              configuration.find((value) => value[0] === config.customId)?.[1]
          );
          if (option && option.hasRequiredImagesModifier) {
            this.images.imagesRequired = option.hasRequiredImagesModifier;
            this.images.requiredImages = option.requiredImagesModifier;
          } else {
            this.images.imagesRequired = this.product.imagesRequired;
            this.images.requiredImages = this.product.requiredImages;
          }
        })
      )
    );
  }

  addToBasket(photoIdList = this.photoIdList) {
    this.store.dispatch(
      addProductToBasketAction({
        product: {
          productId: this.product.productId,
          selectedProductId: cuid(),
          configuration: normalizeProductConfig(
            this.configuration.value,
            this.product.config
          ),
          quantity: this.quantity || 1,
          photoIdList: [...photoIdList],
          nameClientProduct: '',
          firstAddedFile: !!photoIdList.length,
        },
      })
    );
    this.autoAddFileToBasketTogetherWithProductService.autoAddFileToBasketTogetherWithProduct();
    this.hideDialog.emit();
    const ref = this.dialogService.openFull(this.messageAboutAddedToBasket);
    setTimeout(() => {
      ref.close();
    }, 1000);
  }

  async swapProduct(photoIdList = this.photoIdList) {
    const ref = this.dialogService.openFull(NewProjectNameComponent);
    ref.componentInstance.model.patchValue({
      name: this.nameClientProduct,
    });
    return ref.componentInstance.saved
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((productName) => {
        ref.close();
        this.productsInBasket$.pipe(first()).subscribe((productsInBasket) => {
          const productInBasket = productsInBasket.find(
            ({ selectedProductId }) =>
              selectedProductId === this.selectedProductId
          );
          this.store.dispatch(
            changeProductInBasketAction({
              changeInProduct: {
                selectedProductId: this.selectedProductId,
                configuration: normalizeProductConfig(
                  this.configuration.value,
                  this.product.config
                ),
                nameClientProduct: productName,
                quantity: this.quantity || 1,
                photoIdList: [...photoIdList],
                firstAddedFile: !!photoIdList.length,
              },
              productInBasket,
            })
          );
        });
        this.autoAddFileToBasketTogetherWithProductService.autoAddFileToBasketTogetherWithProduct();
        this.hideDialog.emit();
      }).closed;
  }

  swapToBasket() {
    console.log('ADDING TO BASKET', this.configuration.value);
    if (!this.fixingAttributes) {
      this.checkingNumberOfPhotos();
    } else {
      if (this.tooltipRef) {
        this.tooltipRef.close();
        this.tooltipRef = null;
      }
      this.filtered.pipe(first()).subscribe((filtered) => {
        if (
          !filtered
            .filter(({ type }) => type !== 'boolean')
            .find(({ fixing }) => !fixing)
        ) {
          this.checkingNumberOfPhotos();
        } else {
          this.tooltipRef = this.tooltipService.open(
            'Uzupełnij wszystke atrybuty',
            'above',
            this.saveButton.nativeElement
          );
        }
      });
    }
  }

  checkingNumberOfPhotos() {
    if (
      !this.images.imagesRequired ||
      this.photoIdList.length <= this.images.requiredImages.max
    ) {
      this.swapProduct();
    } else {
      const ref = this.fullscreenService.open<any, { response: string }>({
        content: this.message,
        data: {
          mode: 'warn',
        },
      });
      this.openPhotosList$.pipe(first()).subscribe((changeOption) => {
        if (changeOption) {
          const missingPhotosList = this.dialogService.openFull(
            MissingPhotosListComponent
          );
          this.isPhonePortrait$
            .pipe(takeUntil(missingPhotosList.afterClosed()))
            .subscribe((isPhonePortrait) => {
              missingPhotosList._containerInstance.isBare = isPhonePortrait;
            });
          missingPhotosList.componentInstance.photoIdList = this.photoIdList;
          missingPhotosList.componentInstance.swap = this.swap;
          missingPhotosList.componentInstance.context = this.context;
          missingPhotosList.componentInstance.contextId = this.contextId;
          missingPhotosList.componentInstance.maxPhoto =
            this.images.requiredImages.max;
          missingPhotosList.componentInstance.photosSelectedByPhotographer =
            this.listOfPhotosToChooseFromInProductService.find(
              this.product.productId
            );
          missingPhotosList.componentInstance.productNotPackage = true;
          missingPhotosList.componentInstance.product = this.product;
          missingPhotosList.componentInstance.selectedProductId =
            this.selectedProductId;
          missingPhotosList.componentInstance.configuration =
            this.configuration;
          missingPhotosList.componentInstance.quantity = this.quantity;
          this.productsInBasket$.pipe(first()).subscribe((productsInBasket) => {
            missingPhotosList.componentInstance.firstAddedFile =
              productsInBasket.find(
                ({ selectedProductId }) =>
                  selectedProductId === this.selectedProductId
              ).firstAddedFile;
          });
          missingPhotosList.componentInstance.closeDialog
            .pipe(takeUntil(missingPhotosList.afterClosed()))
            .subscribe(() => {
              missingPhotosList.close();
            });
          missingPhotosList.componentInstance.swapProduct
            .pipe(takeUntil(missingPhotosList.afterClosed()))
            .subscribe((photoIdList) => {
              const newProjectName = this.dialogService.openFull(
                NewProjectNameComponent
              );
              newProjectName
                .afterOpened()
                .pipe(first())
                .subscribe(() => missingPhotosList.close());
              newProjectName.componentInstance.model.patchValue({
                name: this.nameClientProduct,
              });
              newProjectName.componentInstance.saved
                .pipe(takeUntil(newProjectName.afterClosed()))
                .subscribe((productName) => {
                  newProjectName.close();
                  this.productsInBasket$
                    .pipe(first())
                    .subscribe((productsInBasket) => {
                      const productInBasket = productsInBasket.find(
                        ({ selectedProductId }) =>
                          selectedProductId === this.selectedProductId
                      );
                      this.store.dispatch(
                        changeProductInBasketAction({
                          changeInProduct: {
                            selectedProductId: this.selectedProductId,
                            configuration: normalizeProductConfig(
                              this.configuration.value,
                              this.product.config
                            ),
                            nameClientProduct: productName,
                            quantity: this.quantity || 1,
                            firstAddedFile: !!photoIdList.length,
                            photoIdList,
                          },
                          productInBasket,
                        })
                      );
                    });
                  this.autoAddFileToBasketTogetherWithProductService.autoAddFileToBasketTogetherWithProduct();
                  this.hideDialog.emit();
                });
            });
        }
        ref.close();
      });
    }
  }

  addMissingPhotos() {
    if (!this.fixingAttributes) {
      this.addPhotos();
    } else {
      this.filtered.pipe(first()).subscribe((filtered) => {
        if (this.tooltipRef) {
          this.tooltipRef.close();
          this.tooltipRef = null;
        }
        if (
          !filtered
            .filter(({ type }) => type !== 'boolean')
            .find(({ fixing }) => !fixing)
        ) {
          this.addPhotos();
        } else {
          this.tooltipRef = this.tooltipService.open(
            'Uzupełnij wszystke atrybuty',
            'above',
            this.photoSelectButton.nativeElement
          );
        }
      });
    }
  }

  addPhotos() {
    const missingPhotosList = this.dialogService.openFull(
      MissingPhotosListComponent
    );
    this.isPhonePortrait$
      .pipe(takeUntil(missingPhotosList.afterClosed()))
      .subscribe((isPhonePortrait) => {
        missingPhotosList._containerInstance.isBare = isPhonePortrait;
      });
    missingPhotosList.componentInstance.photoIdList = this.photoIdList;
    missingPhotosList.componentInstance.context = this.context;
    missingPhotosList.componentInstance.contextId = this.contextId;
    missingPhotosList.componentInstance.swap = this.swap;
    missingPhotosList.componentInstance.product = this.product;
    missingPhotosList.componentInstance.selectedProductId =
      this.selectedProductId;
    missingPhotosList.componentInstance.configuration = this.configuration;
    missingPhotosList.componentInstance.quantity = this.quantity;
    missingPhotosList.componentInstance.productNotPackage = true;
    this.productsInBasket$.pipe(first()).subscribe((productsInBasket) => {
      missingPhotosList.componentInstance.firstAddedFile =
        !!productsInBasket.find(
          ({ selectedProductId }) =>
            selectedProductId === this.selectedProductId
        )?.firstAddedFile;
    });
    missingPhotosList.componentInstance.maxPhoto = this.images.imagesRequired
      ? this.images.requiredImages.max
      : 1;
    missingPhotosList.componentInstance.photosSelectedByPhotographer =
      this.listOfPhotosToChooseFromInProductService.find(
        this.product.productId
      );
    missingPhotosList.componentInstance.closeDialog
      .pipe(takeUntil(missingPhotosList.afterClosed()))
      .subscribe(() => {
        missingPhotosList.close();
      });
    missingPhotosList.componentInstance.addProduct
      .pipe(takeUntil(missingPhotosList.afterClosed()))
      .subscribe((photoIdList) => {
        missingPhotosList.close();
        this.addToBasket(photoIdList);
      });
    missingPhotosList.componentInstance.swapProduct
      .pipe(takeUntil(missingPhotosList.afterClosed()))
      .subscribe((photoIdList) => {
        this.swapProduct(photoIdList).then(() => missingPhotosList.close());
      });
  }
}
